export const INSIGHTS_TIMEFRAME_CHANGE_EVENT = "insights-timeframe-change";

export const CONVERSION_TO_INSIGHTS_TIMEFRAME = {
  "All Time": "all_time",
  "Last 24 Hours": "last_24_hours",
  "Last 7 Days": "last_7_days",
  "Last 30 Days": "last_30_days",
  "Last 90 Days": "last_90_days",
  "Last 365 Days": "last_365_days",
  Today: "today",
  "This Week": "this_week",
  "Last Week": "last_week",
  "This Month": "this_month",
  "This Quarter": "this_quarter",
  "This Year": "this_year",
  "Date Range": "custom",
};

const formatDateOnly = (isoString) => {
  if (!isoString) {
    return null;
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString().slice(0, 10);
};

const yesterdayDateStrings = () => {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const dateStr = yesterday.toISOString().slice(0, 10);

  return { startDate: dateStr, endDate: dateStr };
};

export const buildInsightsTimeframeParams = (
  timeframeLabel,
  rangeStart,
  rangeEnd
) => {
  if (timeframeLabel === "Date Range") {
    return {
      timeframe: "custom",
      start_date: formatDateOnly(rangeStart),
      end_date: formatDateOnly(rangeEnd),
    };
  }

  if (timeframeLabel === "Yesterday") {
    const { startDate, endDate } = yesterdayDateStrings();
    return {
      timeframe: "custom",
      start_date: startDate,
      end_date: endDate,
    };
  }

  return {
    timeframe: CONVERSION_TO_INSIGHTS_TIMEFRAME[timeframeLabel] || "last_30_days",
  };
};

export const buildInsightsUrlWithTimeframe = (
  insightsUrl,
  timeframeLabel,
  rangeStart,
  rangeEnd
) => {
  const url = new URL(insightsUrl, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  currentParams.forEach((value, key) => {
    if (!["timeframe", "start_date", "end_date", "page", "format"].includes(key)) {
      url.searchParams.set(key, value);
    }
  });

  const timeframeParams = buildInsightsTimeframeParams(
    timeframeLabel,
    rangeStart,
    rangeEnd
  );

  url.searchParams.set("timeframe", timeframeParams.timeframe);
  url.searchParams.delete("start_date");
  url.searchParams.delete("end_date");

  if (timeframeParams.start_date) {
    url.searchParams.set("start_date", timeframeParams.start_date);
  }
  if (timeframeParams.end_date) {
    url.searchParams.set("end_date", timeframeParams.end_date);
  }

  return url.toString();
};

export const buildInsightsPdfExportUrl = (insightsUrl) => {
  const url = new URL(insightsUrl, window.location.origin);
  url.search = "";
  url.searchParams.set("format", "pdf");
  return url.toString();
};

export const buildInsightsCsvExportUrl = (insightsUrl) => {
  const url = new URL(insightsUrl, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  currentParams.forEach((value, key) => {
    if (key !== "page" && key !== "format") {
      url.searchParams.set(key, value);
    }
  });

  if (!url.searchParams.get("timeframe")) {
    url.searchParams.set("timeframe", "last_30_days");
  }

  url.searchParams.set("format", "csv");
  url.searchParams.delete("page");

  return url.toString();
};

export const syncInsightsTimeframeToUrl = (
  insightsUrl,
  timeframeLabel,
  rangeStart,
  rangeEnd
) => {
  const nextUrl = buildInsightsUrlWithTimeframe(
    insightsUrl,
    timeframeLabel,
    rangeStart,
    rangeEnd
  );

  window.history.replaceState({}, "", nextUrl);
  window.dispatchEvent(new CustomEvent(INSIGHTS_TIMEFRAME_CHANGE_EVENT));
};

// Value-based sibling of syncInsightsTimeframeToUrl for callers that work with
// raw timeframe values (e.g. "last_30_days") instead of labels.
export const syncInsightsTimeframeParamsToUrl = (
  insightsUrl,
  { timeframe, startDate, endDate }
) => {
  const url = new URL(insightsUrl, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);

  currentParams.forEach((value, key) => {
    if (!["timeframe", "start_date", "end_date", "page", "format"].includes(key)) {
      url.searchParams.set(key, value);
    }
  });

  url.searchParams.set("timeframe", timeframe || "last_30_days");
  if (timeframe === "custom" && startDate) {
    url.searchParams.set("start_date", startDate);
  }
  if (timeframe === "custom" && endDate) {
    url.searchParams.set("end_date", endDate);
  }

  window.history.replaceState({}, "", url.toString());
  window.dispatchEvent(new CustomEvent(INSIGHTS_TIMEFRAME_CHANGE_EVENT));
};
